'use client';

import type { ClassValue } from 'class-variance-authority/types';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { I18N_CONF } from '@funkey/locales/config';
import { type ActivityLocationFilterType } from '@funkey/constants';
import { usePlpExperiment } from '~/features/activities/search/plpExperiment';
import { ActivityCard_Control } from './ControlCard';
import { ActivityCard_Finetuning } from './FinetuningCard';

/** Inputs for building the ActivityCard href.
 *
 * Kept minimal so the helper is pure and trivially testable without pulling in
 * the full portal context shape or component internals. */
export type BuildActivityHrefInput = {
  /** Only WEB may be empty: the public website's default locale is served at the bare path.
   * A portal base path always carries its tenant/area segment. */
  portal: {
    portal: 'WEB';
    baseURLPath: '' | `/${string}`;
  } | {
    portal: 'BIZZ' | 'ADMIN' | 'PARTNER';
    baseURLPath: `/${string}`;
  };
  slug: string;
  id: string | number;
  isPrimary?: boolean | null;
  boostedAdID?: number | null;
  locale: I18N_CONF.Locale;
  /** The participants count selected on the PLP. When set, propagates to the
   *  PDP URL so the PDP reads it from `?participants=` instead of racing
   *  against localStorage hydration (FUN-4441). */
  selectedParticipants?: number | null;
};

/** Builds the href for an ActivityCard link (before any `modal=1` suffix).
 *
 * Handles both the BIZZ booking path and the public translated activities URL.
 * When `selectedParticipants` is a finite number, appends `participants=<n>`
 * to the query string; otherwise appends nothing (the PDP falls back to
 * `activity.minParticipants`). */
export function buildActivityHref(input: BuildActivityHrefInput): string {
  const queryParts: string[] = [];
  if (input.portal.portal === 'BIZZ') {
    queryParts.push(`id=${input.id}`);
    if (typeof input.boostedAdID === 'number') {
      queryParts.push(`boostAdId=${input.boostedAdID}`);
    }
    if (typeof input.selectedParticipants === 'number') {
      queryParts.push(`participants=${input.selectedParticipants}`);
    }
    return `${input.portal.baseURLPath}/booking/${input.slug}?${queryParts.join('&')}`;
  }
  if (!input.isPrimary) queryParts.push(`id=${input.id}`);
  if (typeof input.boostedAdID === 'number') {
    queryParts.push(`boostAdId=${input.boostedAdID}`);
  }
  if (typeof input.selectedParticipants === 'number') {
    queryParts.push(`participants=${input.selectedParticipants}`);
  }
  return I18N_CONF.getTranslatedUrl('activities', {
    slug: input.slug,
    locale: input.locale,
    searchParams: queryParts.length > 0 ? queryParts.join('&') : undefined
  });
}

/** Props shared by both variants of the card. The activity fields come from
 * the search payload; everything after it is how the surrounding grid drives the
 * card. */
export type ActivityCardProps = TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>['activities'][number] & {
  //(typeof action_searchActivities.$output)['activities'][number]
  children?: React.ReactNode;
  className?: ClassValue;
  /** Disables the PDP href on the activity card */
  disableHref?: boolean;
  onClick?(): void;
  boostedAdID?: number;
  /** Show debug styling for boosted ads (when debug parameters are active) */
  debugShowSponsoredStyling?: boolean;
  /** Controls how the activity card link opens */
  openIn?: 'modal' | 'new-tab' | 'current-window';
  /** Dims the image header to indicate an inactive/unselected state */
  inactive?: boolean;
  /** Admin-only AI scores for this card (relevancy + popularity). Set only
   *  by the AI-search grid for super admins; renders a debug badge. */
  adminScore?: {
    similarity: number;
    rankScore: number;
    popularityBoost: number;
  };
  /** Hides the wishlist (heart) button on the card. Used by the bizz
   * catalog edit mode where the heart isn't actionable for the admin. */
  hideWishlist?: boolean;
  /** Participants count selected on the PLP filters, forwarded to the PDP
   *  as `?participants=<n>` so mobile modal nav keeps the value (FUN-4441). */
  selectedParticipants?: number | null;
  /** The group size the server priced `price` at, taken from the same
   *  payload the price came from. Not the live filter value: between a
   *  filter change and the refetch landing those differ, and multiplying
   *  a stale unit price by the new size yields a total for no query at
   *  all. Falls back to the shared default only outside the PLP. */
  pricedParticipants?: number;
  /** Selected route filter (FIXED/CUSTOM). When set, only the route icons that
   *  match the selection are shown on the distance pill (e.g. selecting "partner
   *  comes to me" hides the "I go to the partner" car). Empty/undefined = show all. */
  routeFilter?: readonly ActivityLocationFilterType[];
  /** The searched radius (km). When the partner's fixed location is further than
   *  this, the car pill drops the km figure (it's beyond what was asked) but the
   *  icon stays — the activity is shown because the partner also comes to you. */
  searchRadiusKm?: number | null;
};

/** Picks the card the visitor's experiment group should see. The two variants
 * are whole components rather than branches inside one, so the control renders
 * exactly what production renders today and the losing variant can be deleted
 * as a file once the experiment settles. */
export const ActivityCard: React.FC<ActivityCardProps> = props => {
  return usePlpExperiment() ? <ActivityCard_Finetuning {...props} /> : <ActivityCard_Control {...props} />;
};